import { useState, useEffect, useRef } from 'react'
import {
  Shield, FileCheck, Users, TrendingUp, ArrowRight, Check, Star,
  Lock, Zap, Globe, ChevronDown, Phone, MessageCircle, Instagram,
  Music, Sparkles, Crown, Award, Rocket, BarChart3,
  Building2, Wallet, Scale, Briefcase, PlayCircle,
  ChevronRight, ExternalLink, Coffee, Sun, Moon
} from 'lucide-react'

// ✅ Premium SVG illustrations (no cheap stock images)
const HeroIllustration = () => (
  <div className="relative w-full max-w-lg mx-auto">
    <div className="absolute inset-0 bg-gradient-to-br from-brand/10 to-emerald-500/10 rounded-3xl blur-3xl" />
    <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-brand to-brand-dark rounded-xl flex items-center justify-center shadow-lg">
          <Shield size={24} className="text-white" />
        </div>
        <div>
          <p className="text-xs font-semibold text-muted uppercase tracking-wider">Governance Score</p>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-ink">92%</span>
            <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">+12%</span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {[
          { label: 'Corporate Documents', value: 18, total: 24, color: 'bg-brand' },
          { label: 'Financial Records', value: 12, total: 15, color: 'bg-emerald-500' },
          { label: 'Compliance Status', value: 8, total: 10, color: 'bg-amber-500' },
        ].map((item) => (
          <div key={item.label}>
            <div className="flex justify-between text-xs">
              <span className="text-muted">{item.label}</span>
              <span className="text-ink font-medium">{item.value}/{item.total}</span>
            </div>
            <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${item.color} rounded-full transition-all duration-1000`}
                style={{ width: `${(item.value / item.total) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-6 border-t border-neutral-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
            <Check size={14} className="text-emerald-600" />
          </div>
          <span className="text-xs text-muted">Investor Ready</span>
        </div>
        <button className="text-xs font-semibold text-brand hover:text-brand-dark transition-colors">
          View Details →
        </button>
      </div>
    </div>

    {/* Floating badges */}
    <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl p-3 animate-float">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
          <Crown size={14} className="text-amber-600" />
        </div>
        <div>
          <p className="text-[10px] text-muted">Venture Grade</p>
          <p className="text-xs font-bold text-ink">Top 5%</p>
        </div>
      </div>
    </div>
    <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl p-3 animate-float-delayed">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
          <Rocket size={14} className="text-emerald-600" />
        </div>
        <div>
          <p className="text-[10px] text-muted">Due Diligence</p>
          <p className="text-xs font-bold text-ink">Completed</p>
        </div>
      </div>
    </div>
  </div>
)

// ✅ Dashboard preview illustration
const DashboardPreview = () => (
  <div className="relative w-full max-w-2xl mx-auto">
    <div className="absolute inset-0 bg-gradient-to-br from-brand/5 to-emerald-500/5 rounded-3xl blur-2xl" />
    <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-neutral-100">
      {/* Mock header */}
      <div className="bg-ink px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
            <Shield size={14} className="text-white" />
          </div>
          <span className="text-white font-semibold text-sm">Bizos Suite</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-neutral-700 rounded-full" />
          <div className="w-6 h-6 bg-neutral-700 rounded-full" />
        </div>
      </div>

      {/* Mock content */}
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted">Welcome back</p>
            <h4 className="text-lg font-bold text-ink">Acme Innovations Ltd</h4>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <Check size={14} className="text-emerald-600" />
            </div>
            <span className="text-xs font-semibold text-emerald-700">92%</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Income', value: '₦45.2M', change: '+12%', color: 'text-emerald-600' },
            { label: 'Expenses', value: '₦32.1M', change: '-5%', color: 'text-rose-600' },
            { label: 'Profit', value: '₦13.1M', change: '+28%', color: 'text-brand' },
          ].map((item) => (
            <div key={item.label} className="bg-neutral-50 rounded-xl p-3">
              <p className="text-[10px] text-muted">{item.label}</p>
              <p className="text-sm font-bold text-ink">{item.value}</p>
              <p className={`text-[10px] font-semibold ${item.color}`}>{item.change}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
            <div className="h-full bg-brand rounded-full" style={{ width: '78%' }} />
          </div>
          <span className="text-xs font-mono text-muted">78%</span>
        </div>
      </div>
    </div>
  </div>
)

// ✅ Premium features with icons
const FEATURES = [
  {
    icon: Shield,
    title: 'Compliance Vault',
    desc: 'Bank-grade encrypted storage for every corporate document. Access controlled, always audit-ready.',
    gradient: 'from-brand to-brand-dark'
  },
  {
    icon: FileCheck,
    title: 'Smart Verification',
    desc: 'AI-powered document validation with real-time governance scoring. Know your readiness instantly.',
    gradient: 'from-emerald-500 to-emerald-600'
  },
  {
    icon: Crown,
    title: 'Investor Portal',
    desc: 'Secure, time-limited vault access for investors. Full transparency with complete control.',
    gradient: 'from-amber-500 to-amber-600'
  },
  {
    icon: TrendingUp,
    title: 'Readiness Scoring',
    desc: 'Proprietary algorithm that flags gaps before investors find them. Always stay ahead.',
    gradient: 'from-blue-500 to-blue-600'
  },
  {
    icon: Lock,
    title: 'Audit Trail',
    desc: 'Immutable blockchain-backed audit logs. Every action tracked, every stakeholder accountable.',
    gradient: 'from-purple-500 to-purple-600'
  },
  {
    icon: Globe,
    title: 'Global Compliance',
    desc: 'Multi-jurisdictional support. Nigeria, US, UK, Kenya, South Africa — all in one platform.',
    gradient: 'from-teal-500 to-teal-600'
  },
]

// ✅ Premium stats
const STATS = [
  { value: '500+', label: 'Companies Onboarded', icon: Building2 },
  { value: '12K+', label: 'Documents Verified', icon: FileCheck },
  { value: '98%', label: 'Due Diligence Pass Rate', icon: TrendingUp },
  { value: '4.9★', label: 'Average Rating', icon: Star },
]

// ✅ Premium testimonials
const TESTIMONIALS = [
  {
    name: 'Adaeze Okonkwo',
    role: 'CEO, GreenPath Agric',
    quote: 'Bizos Suite transformed how we prepare for investor due diligence. Our governance score went from 40% to 92% in three months.',
    rating: 5,
    image: 'AE'
  },
  {
    name: 'Tunde Bakare',
    role: 'CFO, Lagos Energy Co.',
    quote: 'The investor portal alone closed our Series A. Investors could verify everything without chasing us for documents.',
    rating: 5,
    image: 'TB'
  },
  {
    name: 'Fatima Ibrahim',
    role: 'Founder, Northern Logistics',
    quote: 'We used to spend weeks gathering compliance documents. Now it takes minutes. The audit trail gives investors confidence.',
    rating: 5,
    image: 'FI'
  },
]

// ✅ Target audiences
const AUDIENCES = [
  {
    icon: Rocket,
    title: 'Startups Raising Capital',
    desc: 'Prepare for due diligence before investors ask. Show a verified governance score that sets you apart.',
    gradient: 'from-brand to-brand-dark'
  },
  {
    icon: Building2,
    title: 'SMEs & Growing Companies',
    desc: 'Stay on top of CAC filings, tax clearance, and sector permits without spreadsheets and scattered folders.',
    gradient: 'from-emerald-500 to-emerald-600'
  },
  {
    icon: Users,
    title: 'Investors & Fund Managers',
    desc: 'Access verified company vaults with confidence. Every document is authenticated and audit-trail backed.',
    gradient: 'from-amber-500 to-amber-600'
  },
  {
    icon: Briefcase,
    title: 'Compliance Agents',
    desc: 'Offer document preparation and verification services through our agent network. Earn while you help companies stay compliant.',
    gradient: 'from-blue-500 to-blue-600'
  },
]

// ✅ Custom hook for intersection observer
function useInView(threshold = 0.15) {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true)
    }, { threshold })
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, inView]
}

function FadeIn({ children, delay = 0, className = '' }) {
  const [ref, inView] = useInView()
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  )
}

export default function Landing({ onNavigate, onGetStarted }) {
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* ============================================================
          NAVIGATION - Premium
          ============================================================ */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-white/95 backdrop-blur-xl shadow-sm border-b border-neutral-100' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-brand to-brand-dark rounded-xl flex items-center justify-center shadow-lg">
              <Shield size={18} className="text-white" />
            </div>
            <span className={`font-bold text-lg tracking-tight transition-colors ${
              scrolled ? 'text-ink' : 'text-ink'
            }`}>
              Bizos Suite
            </span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => onNavigate('about')} className={`text-sm font-medium hover:text-brand transition-colors ${
              scrolled ? 'text-neutral-600' : 'text-neutral-600'
            }`}>
              About
            </button>
            <button onClick={() => onNavigate('pricing')} className={`text-sm font-medium hover:text-brand transition-colors ${
              scrolled ? 'text-neutral-600' : 'text-neutral-600'
            }`}>
              Pricing
            </button>
            <button onClick={() => onNavigate('contact')} className={`text-sm font-medium hover:text-brand transition-colors ${
              scrolled ? 'text-neutral-600' : 'text-neutral-600'
            }`}>
              Contact
            </button>
            <div className="w-px h-6 bg-neutral-200" />
            <button onClick={() => onNavigate('agent-registration')} className="text-sm font-medium text-brand hover:text-brand-dark transition-colors">
              Become an Agent
            </button>
            <button onClick={onGetStarted} className="text-sm font-semibold text-ink hover:text-brand transition-colors">
              Sign In
            </button>
            <button onClick={() => onNavigate('pricing')} className="btn-primary text-sm shadow-lg shadow-brand/20 hover:shadow-brand/40 transition-shadow">
              Get Started
            </button>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2">
            <ChevronDown size={20} className={`transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-neutral-100 px-6 py-4 space-y-3">
            <button onClick={() => onNavigate('about')} className="block text-sm font-medium text-neutral-700 w-full text-left">About</button>
            <button onClick={() => onNavigate('pricing')} className="block text-sm font-medium text-neutral-700 w-full text-left">Pricing</button>
            <button onClick={() => onNavigate('contact')} className="block text-sm font-medium text-neutral-700 w-full text-left">Contact</button>
            <button onClick={() => onNavigate('agent-registration')} className="block text-sm font-medium text-brand w-full text-left">Become an Agent</button>
            <button onClick={onGetStarted} className="block text-sm font-semibold text-brand w-full text-left">Sign In</button>
            <button onClick={() => onNavigate('pricing')} className="btn-primary text-sm w-full">Get Started</button>
          </div>
        )}
      </nav>

      {/* ============================================================
          HERO - Premium
          ============================================================ */}
      <section className="relative min-h-screen pt-32 pb-20 px-6 overflow-hidden flex items-center">
        {/* Background gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-50 via-white to-neutral-100" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-brand-soft rounded-full blur-3xl opacity-20" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-50 rounded-full blur-3xl opacity-30" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center w-full">
          <div>
            <FadeIn>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-soft text-brand text-xs font-semibold rounded-full border border-brand/10">
                <Sparkles size={12} /> Built for African Businesses
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-ink leading-[1.1] tracking-tight mt-4">
                Governance readiness,<br />
                <span className="bg-gradient-to-r from-brand to-emerald-500 bg-clip-text text-transparent">simplified.</span>
              </h1>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-6 text-lg text-neutral-600 leading-relaxed max-w-lg">
                The all-in-one platform for compliance management, document verification,
                and investor due diligence preparation. Built for Nigerian and pan-African companies.
              </p>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="mt-8 flex flex-wrap gap-4">
                <button onClick={() => onNavigate('pricing')} className="btn-primary flex items-center gap-2 shadow-lg shadow-brand/25 hover:shadow-brand/40 transition-shadow">
                  Start Free Trial <ArrowRight size={16} />
                </button>
                {/* ✅ FIXED: Changed from 'about' to 'demo' */}
                <button onClick={() => onNavigate('demo')} className="btn-secondary flex items-center gap-2">
                  <PlayCircle size={16} /> Watch Demo
                </button>
              </div>
            </FadeIn>
            <FadeIn delay={0.4}>
              <div className="mt-8 flex items-center gap-6 text-sm text-neutral-500">
                <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600" /> No credit card required</span>
                <span className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600" /> 14-day free trial</span>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.3}>
            <HeroIllustration />
          </FadeIn>
        </div>
      </section>

      {/* ============================================================
          STATS - Premium
          ============================================================ */}
      <section className="py-16 px-6 bg-ink">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((stat, i) => {
            const Icon = stat.icon
            return (
              <FadeIn key={stat.label} delay={i * 0.1}>
                <div className="text-center group">
                  <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-white/10 transition-colors">
                    <Icon size={20} className="text-brand" />
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-white">{stat.value}</p>
                  <p className="mt-1 text-sm text-neutral-400">{stat.label}</p>
                </div>
              </FadeIn>
            )
          })}
        </div>
      </section>

      {/* ============================================================
          FEATURES - Premium
          ============================================================ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-soft text-brand text-xs font-semibold rounded-full border border-brand/10 mb-4">
                <Zap size={12} /> Features
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-ink tracking-tight">
                Everything you need to be <span className="text-brand">investor-ready</span>
              </h2>
              <p className="mt-4 text-lg text-neutral-600">
                From document management to investor access — all in one platform.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, i) => {
              const Icon = feature.icon
              return (
                <FadeIn key={feature.title} delay={(i % 3) * 0.1}>
                  <div className="group p-8 rounded-2xl border border-neutral-200 hover:border-brand/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-5 shadow-lg group-hover:scale-105 transition-transform`}>
                      <Icon size={24} className="text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-ink mb-2">{feature.title}</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{feature.desc}</p>
                  </div>
                </FadeIn>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          DASHBOARD PREVIEW - Premium
          ============================================================ */}
      <section className="py-24 px-6 bg-neutral-50">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full border border-emerald-200 mb-4">
                <BarChart3 size={12} /> Dashboard
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-ink tracking-tight">
                A dashboard <span className="text-brand">investors trust</span>
              </h2>
              <p className="mt-4 text-lg text-neutral-600">
                Every document verified. Every action logged. Every score backed by evidence.
              </p>
            </div>
          </FadeIn>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <div className="space-y-6">
                {[
                  'Real-time governance and readiness scores',
                  'Document queue with admin verification',
                  'Investor vault with time-limited access tokens',
                  'Full audit trail for every action',
                  'Automated document reminders and alerts',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 group">
                    <div className="w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center shrink-0 mt-0.5 group-hover:scale-110 transition-transform">
                      <Check size={14} className="text-emerald-600" />
                    </div>
                    <span className="text-neutral-700">{item}</span>
                  </div>
                ))}
                <button onClick={onGetStarted} className="btn-primary mt-6 flex items-center gap-2 shadow-lg shadow-brand/20 hover:shadow-brand/40 transition-shadow">
                  See it in action <ArrowRight size={16} />
                </button>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <DashboardPreview />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ============================================================
          TARGET AUDIENCES - Premium
          ============================================================ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-600 text-xs font-semibold rounded-full border border-amber-200 mb-4">
                <Users size={12} /> Audience
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-ink tracking-tight">
                Who is <span className="text-brand">Bizos Suite</span> for?
              </h2>
              <p className="mt-4 text-lg text-neutral-600">
                Whether you're raising capital, managing compliance, or investing — we've built something for you.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-6">
            {AUDIENCES.map((audience, i) => {
              const Icon = audience.icon
              return (
                <FadeIn key={audience.title} delay={(i % 2) * 0.1}>
                  <div className="group p-8 rounded-2xl border border-neutral-200 hover:border-brand/20 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${audience.gradient} flex items-center justify-center mb-5 shadow-lg group-hover:scale-105 transition-transform`}>
                      <Icon size={24} className="text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-ink mb-2">{audience.title}</h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">{audience.desc}</p>
                  </div>
                </FadeIn>
              )
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          TESTIMONIALS - Premium
          ============================================================ */}
      <section className="py-24 px-6 bg-ink">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white text-xs font-semibold rounded-full border border-white/10 mb-4">
                <Star size={12} /> Testimonials
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                Trusted by <span className="text-brand">African founders</span>
              </h2>
              <p className="mt-4 text-lg text-neutral-400">
                See what companies using Bizos Suite have to say.
              </p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.1}>
                <div className="p-8 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-300">
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, idx) => (
                      <Star key={idx} size={16} className="text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-neutral-300 leading-relaxed mb-6">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-brand/20 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {t.image}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{t.name}</p>
                      <p className="text-xs text-neutral-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          CTA - Premium
          ============================================================ */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <div className="relative p-12 md:p-16 rounded-3xl bg-gradient-to-br from-ink to-neutral-800 overflow-hidden">
              {/* Background effects */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />

              <div className="relative text-center">
                <div className="w-16 h-16 bg-brand/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Rocket size={28} className="text-brand" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
                  Ready to become <span className="text-brand">investor-ready</span>?
                </h2>
                <p className="mt-4 text-lg text-neutral-300 max-w-lg mx-auto">
                  Start your 14-day free trial. No credit card required. Cancel anytime.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-4">
                  <button onClick={() => onNavigate('pricing')} className="btn-primary flex items-center gap-2 shadow-lg shadow-brand/25 hover:shadow-brand/40 transition-shadow">
                    View Pricing <ArrowRight size={16} />
                  </button>
                  <button onClick={() => onNavigate('contact')} className="px-6 py-3 border border-white/20 text-white rounded-lg font-semibold hover:bg-white/10 transition-colors flex items-center gap-2">
                    Talk to Us <ChevronRight size={16} />
                  </button>
                </div>
                <div className="mt-6 flex items-center justify-center gap-6 text-xs text-neutral-500">
                  <span className="flex items-center gap-1.5"><Check size={12} className="text-emerald-500" /> No credit card</span>
                  <span className="flex items-center gap-1.5"><Check size={12} className="text-emerald-500" /> 14-day trial</span>
                  <span className="flex items-center gap-1.5"><Check size={12} className="text-emerald-500" /> Cancel anytime</span>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ============================================================
          FOOTER - Premium
          ============================================================ */}
      <footer className="py-16 px-6 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-9 h-9 bg-gradient-to-br from-brand to-brand-dark rounded-xl flex items-center justify-center shadow-lg">
                  <Shield size={18} className="text-white" />
                </div>
                <span className="font-bold text-lg text-ink">Bizos Suite</span>
              </div>
              <p className="text-sm text-neutral-500 max-w-sm">
                Governance readiness for African businesses. Built for Nigeria, expanding globally.
              </p>
              <div className="flex items-center gap-4 mt-4">
                <a href="https://instagram.com/officialbizos" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-xl border border-neutral-200 flex items-center justify-center hover:border-brand hover:text-brand transition-colors">
                  <Instagram size={18} className="text-neutral-500 group-hover:text-brand" />
                </a>
                <a href="https://wa.me/2348142243764" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-xl border border-neutral-200 flex items-center justify-center hover:border-brand hover:text-brand transition-colors">
                  <MessageCircle size={18} className="text-neutral-500 group-hover:text-brand" />
                </a>
                <a href="https://tiktok.com/@officialbizos" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white rounded-xl border border-neutral-200 flex items-center justify-center hover:border-brand hover:text-brand transition-colors">
                  <Music size={18} className="text-neutral-500 group-hover:text-brand" />
                </a>
                <a href="tel:08142243764" className="w-10 h-10 bg-white rounded-xl border border-neutral-200 flex items-center justify-center hover:border-brand hover:text-brand transition-colors">
                  <Phone size={18} className="text-neutral-500 group-hover:text-brand" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-ink mb-3">Product</h4>
              <div className="space-y-2">
                <button onClick={() => onNavigate('pricing')} className="block text-sm text-neutral-500 hover:text-brand transition-colors">Pricing</button>
                <button onClick={() => onNavigate('about')} className="block text-sm text-neutral-500 hover:text-brand transition-colors">About</button>
                <button onClick={() => onNavigate('contact')} className="block text-sm text-neutral-500 hover:text-brand transition-colors">Contact</button>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-ink mb-3">Company</h4>
              <div className="space-y-2">
                <button onClick={() => onNavigate('about')} className="block text-sm text-neutral-500 hover:text-brand transition-colors">About Us</button>
                <button onClick={() => onNavigate('agent-registration')} className="block text-sm text-neutral-500 hover:text-brand transition-colors">Become an Agent</button>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-ink mb-3">Legal</h4>
              <div className="space-y-2">
                <span className="block text-sm text-neutral-500">Privacy Policy</span>
                <span className="block text-sm text-neutral-500">Terms of Service</span>
                <span className="block text-sm text-neutral-500">NDPR Compliant</span>
              </div>
            </div>
          </div>
          <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-neutral-200">
            <p className="text-xs text-neutral-400 text-center">
              © 2026 Bizos Suite. All rights reserved. Built in Nigeria for Africa. Powered by Vobels Limited.
            </p>
          </div>
        </div>
      </footer>

      {/* ============================================================
          ADDITIONAL ANIMATIONS CSS
          ============================================================ */}
      <style>{`
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-float-delayed {
          animation: float-delayed 3.5s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}